import { useCallback, useMemo, useRef, useState, useEffect } from 'react';
import '../ConfigReportesDefiniciones';
import Popup from 'devextreme-react/popup';
import { useApiConfiguration } from '../../../api/api.configuration';
import { useScreenSize } from '../../../utils/media-query';
import DataGrid, { Column, Button as Butt, Editing, Item as Items, KeyboardNavigation, Paging, Lookup, Scrolling, Sorting, StateStoring, Selection, FilterRow, RequiredRule } from 'devextreme-react/data-grid';
import DataSource from 'devextreme/data/data_source';
import CustomStore from 'devextreme/data/custom_store';
import { Cast, SystemFields, Tablas, Types } from '../../../types/types.config';
import { useAppSelector } from '../../../contexts/hooks';

export const ModalDefiniciones = ({ visible, setVisible, Id }: { visible: boolean; setVisible: (value: boolean) => void; Id: number }) => {
  const { getDefinicionesById, setDefiniciones, getDefiniciones } = useApiConfiguration();
  const [data, setData] = useState<SystemFields[]>([]);
  const { isMedium, isSmall } = useScreenSize();
  const dataRef = useRef<DataGrid>(null);
  const lstComboBox = useAppSelector((x) => x.configuration.Data?.ComboBox);

  // Extraer solo id y nombre
  const comboBoxData =
    lstComboBox?.map((item) => ({
      id: item.Id,
      nombre: item.Name,
    })) || [];

  useEffect(() => {
    getDefiniciones().then((res) => {
      setData(res);
    });
  }, []);

  const customData = useMemo(() => {
    return new CustomStore({
      key: 'Dfc_Id',
      load() {
        return getDefinicionesById(Id)
          .then((res) => ({
            data: res,
            totalCount: res.totalCount,
            summary: res.summary,
            groupCount: res.groupCount,
          }))
          .catch(() => {
            throw new Error('Error de carga de datos');
          });
      },
      update: (key, values) => {
        const currentItem = data.find((item) => item.Dfc_Id === key);

        if (!currentItem) {
          throw new Error('No se encontró la definición para actualizar');
        }

        const updatedItem = { ...currentItem, ...values };

        return setDefiniciones({ Definicion: updatedItem, IdDefinicion: key })
          .then((res) => {
            if (res === 107) {
              return getDefiniciones().then(setData);
            }
          })
          .catch(() => {
            throw new Error('Error actualizando la definición');
          });
      },
    });
  }, [Id, data]);

  const customDataSrc = new DataSource({
    store: customData,
    reshapeOnPush: true,
  });

  const handleVisible = useCallback(() => {
    setVisible(false);
  }, []);

  return (
    <Popup width={1200} height={'auto'} fullScreen={isMedium || isSmall} visible={visible} onHidden={handleVisible} showTitle={true} showCloseButton={true}>
      <div>
        <DataGrid className='grid' dataSource={customDataSrc} ref={dataRef} hoverStateEnabled={true} height='auto' repaintChangesOnly={true} highlightChanges={true} rowAlternationEnabled={true} showColumnLines={true}>
          <Scrolling columnRenderingMode='virtual' />
          <Sorting mode='multiple' />
          <Selection mode='single' />
          <KeyboardNavigation editOnKeyPress={true} enterKeyAction='startEdit' enterKeyDirection='column' />
          <Paging enabled={false} />
          <StateStoring enabled={true} type='custom' />
          <Editing mode='popup' selectTextOnEditStart={true} allowUpdating={true} />
          <Column caption='ID' allowEditing={false} visible={true} dataField='Dfc_Id' alignment='center' width={50} />
          <Column allowGrouping={false} caption='TIPO' visible={true} allowEditing={true} dataField='Dfc_Tcm_Id'>
            <Lookup dataSource={Types} valueExpr='id' displayExpr='name' />
            <RequiredRule />
          </Column>
          <Column allowGrouping={false} caption='COMBO BOX' visible={true} allowEditing={true} dataField='Dfc_Tbc_Id'>
            <Lookup dataSource={comboBoxData} valueExpr='id' displayExpr='nombre' />
            <RequiredRule />
          </Column>
          <Column allowGrouping={false} caption='LABEL' visible={true} allowEditing={true} dataField='Dfc_Label'>
            <RequiredRule />
          </Column>
          <Column allowGrouping={false} caption='TABLA' visible={true} allowEditing={true} dataField='Dfc_Tabla'>
            <Lookup dataSource={Tablas} valueExpr='name' displayExpr='name' />
            <RequiredRule />
          </Column>
          <Column allowGrouping={false} caption='EQUIVALENCIA' visible={true} allowEditing={true} dataField='Dfc_Equivalencia'>
            <RequiredRule />
          </Column>
          <Column caption='REQUERIDO' allowEditing={true} visible={true} dataField='Dfc_Requerido' alignment='center' width={90}></Column>
          <Column caption='EDITABLE' allowEditing={true} visible={true} dataField='Dfc_Editable' alignment='center' width={90}></Column>
          <Column allowGrouping={false} caption='MINIMO' visible={true} allowEditing={true} dataField='Dfc_Minimo'>
            <RequiredRule />
          </Column>
          <Column allowGrouping={false} caption='MAXIMO' visible={true} allowEditing={true} dataField='Dfc_Maximo'>
            <RequiredRule />
          </Column>
          <Column allowGrouping={false} caption='ORDEN' visible={true} allowEditing={true} dataField='Dfc_Orden'>
            <RequiredRule />
          </Column>
          <Column allowGrouping={false} caption='NRO COLUMNAS' visible={true} allowEditing={true} dataField='Dfc_NroColumnas'>
            <RequiredRule />
          </Column>
          <Column caption='DINAMICO' allowEditing={true} visible={true} dataField='Dfc_Dinamico' alignment='center' width={90}></Column>
          <Column allowGrouping={false} caption='MASCARA' visible={true} allowEditing={true} dataField='Dfc_Mascara'></Column>
          <Column caption='ESTADO' allowEditing={true} visible={true} dataField='Dfc_Estado' alignment='center' width={90}></Column>
          <Column allowGrouping={false} allowSorting={false} visible={true} caption='CAST' allowEditing={true} dataField='Dfc_Cast' alignment='center' width={90}>
            <Lookup dataSource={Cast} valueExpr='name' displayExpr='name' />
          </Column>
          <Column allowGrouping={false} caption='DEPENDENCIA' visible={true} allowEditing={true} dataField='Dfc_Dependency'></Column>
          <Column allowGrouping={false} caption='DEPENDENCIA CAMPO' visible={true} allowEditing={true} dataField='Dfc_DependencyField'></Column>
          <Column allowGrouping={false} caption='DEPENDENCIA DEFINICION' visible={true} allowEditing={true} dataField='Dfc_DependencyDefinition'></Column>
          <Column caption='VISIBLE' allowEditing={true} visible={true} dataField='Dfc_Visible' alignment='center' width={90}></Column>
        </DataGrid>
      </div>
    </Popup>
  );
};
